import { supabase } from "../supabase-config";
import { withLocalWorkImage } from "../assets/workImages";

export const ADMIN_COLLECTIONS = [
  { key: "documentary", label: "Film & Doc" },
  { key: "commercial", label: "Branded Content" },
  { key: "press", label: "Press" },
  { key: "lunatik", label: "Lunatik" },
  { key: "credits", label: "Credits" },
];

export const LANDING_MEDIA_COLLECTION = "landing_media";

export const LANDING_MEDIA_SLOTS = [
  {
    id: "quote_a",
    label: "Quote left",
    hint: "Small photo that passes on the left of the quote.",
  },
  {
    id: "quote_b",
    label: "Quote right",
    hint: "Small photo that passes on the right of the quote.",
  },
  {
    id: "magazine",
    label: "Magazine image",
    hint: "Full-bleed image in the “Let’s talk” section.",
  },
];

const DATA_BASE = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/media/data`;

function sortByOrder(a, b) {
  const ao = Number(a.sort_order ?? a.sortOrder ?? 0);
  const bo = Number(b.sort_order ?? b.sortOrder ?? 0);
  if (ao !== bo) return ao - bo;
  return String(a.id).localeCompare(String(b.id));
}

function isSchemaCacheError(error) {
  return /schema cache|could not find the '/i.test(error?.message || "");
}

export function toUiItem(row, collection) {
  if (!row) return row;
  const item = {
    id: row.id,
    title: row.title,
    description: row.description,
    link: row.link ?? row.Link ?? "",
    producer: row.producer ?? row.Producer ?? null,
    date: row.date ?? row.Date ?? null,
    ImageUrl: row.image_url ?? row.ImageUrl ?? null,
    ImageIsScoresHome:
      row.image_is_scores_home != null
        ? String(row.image_is_scores_home)
        : row.ImageIsScoresHome,
    sort_order:
      row.sort_order != null
        ? Number(row.sort_order)
        : row.sortOrder != null
          ? Number(row.sortOrder)
          : 0,
  };
  return withLocalWorkImage(item, collection);
}

export function toDbRow(data) {
  const row = {
    id: String(data.id),
    title: data.title ?? null,
    description: data.description ?? null,
    link: data.link ?? "",
    producer: data.producer ?? null,
    date: data.date ?? null,
    image_url: data.ImageUrl ?? data.image_url ?? null,
    sort_order:
      data.sort_order != null
        ? Number(data.sort_order)
        : data.sortOrder != null
          ? Number(data.sortOrder)
          : 0,
  };
  if (data.ImageIsScoresHome != null) {
    row.image_is_scores_home =
      String(data.ImageIsScoresHome).toLowerCase() === "true";
  }
  return row;
}

function toJsonDoc(item) {
  const out = {
    id: String(item.id),
    title: item.title ?? null,
    link: item.link || "",
    ImageUrl: item.ImageUrl ?? item.image_url ?? null,
    sort_order:
      item.sort_order != null
        ? Number(item.sort_order)
        : item.sortOrder != null
          ? Number(item.sortOrder)
          : 0,
  };
  if (item.description != null) out.description = item.description;
  if (item.producer != null) out.producer = item.producer;
  if (item.date != null) out.date = item.date;
  if (item.ImageIsScoresHome != null) {
    out.ImageIsScoresHome = item.ImageIsScoresHome;
  }
  return out;
}

async function fetchJsonCollection(collection) {
  const res = await fetch(`${DATA_BASE}/${collection}.json?t=${Date.now()}`);
  if (!res.ok) return [];
  const json = await res.json();
  return (Array.isArray(json) ? json : [])
    .map((row) => toUiItem(row, collection))
    .map((item, index) => ({
      ...item,
      sort_order: item.sort_order ?? index,
    }))
    .sort(sortByOrder);
}

async function writeJsonCollection(collection, items) {
  const docs = items.map((item, index) =>
    toJsonDoc({ ...item, sort_order: index })
  );
  const body = new Blob([JSON.stringify(docs, null, 2)], {
    type: "application/json",
  });
  const { error } = await supabase.storage
    .from("media")
    .upload(`data/${collection}.json`, body, {
      upsert: true,
      contentType: "application/json",
    });
  if (error) throw error;
}

async function tablesAvailable(collection) {
  const { error } = await supabase.from(collection).select("id").limit(1);
  return !error;
}

async function selectOrdered(collection) {
  const withOrder = await supabase
    .from(collection)
    .select("*")
    .order("sort_order", { ascending: true })
    .order("id", { ascending: true });

  if (!withOrder.error) {
    return Array.isArray(withOrder.data) ? withOrder.data : [];
  }

  const fallback = await supabase.from(collection).select("*").order("id", {
    ascending: true,
  });
  if (fallback.error) throw fallback.error;
  return Array.isArray(fallback.data) ? fallback.data : [];
}

async function upsertRow(collection, row) {
  let payload = { ...row };
  for (let i = 0; i < 8; i += 1) {
    const { error } = await supabase
      .from(collection)
      .upsert(payload, { onConflict: "id" });
    if (!error) return;
    const match = /Could not find the '([^']+)' column/i.exec(
      error.message || ""
    );
    if (match && match[1] in payload) {
      delete payload[match[1]];
      continue;
    }
    if (isSchemaCacheError(error)) {
      const next = { ...payload };
      delete next.sort_order;
      delete next.image_is_scores_home;
      if (JSON.stringify(next) === JSON.stringify(payload)) throw error;
      payload = next;
      continue;
    }
    throw error;
  }
  throw new Error("Could not save to table");
}

async function syncTableBestEffort(collection, items) {
  if (!(await tablesAvailable(collection))) return;
  try {
    await Promise.all(items.map((item) => upsertRow(collection, toDbRow(item))));
  } catch {
    // JSON is the source of truth while Postgres schema/RLS catch up.
  }
}

export async function listCollection(collection) {
  const json = await fetchJsonCollection(collection);
  if (await tablesAvailable(collection)) {
    try {
      const data = await selectOrdered(collection);
      if (data.length >= json.length && data.length) {
        return data.map((row) => toUiItem(row, collection)).sort(sortByOrder);
      }
    } catch {
      // fall through to JSON
    }
  }
  return json;
}

export async function upsertItem(collection, item) {
  const ui = toUiItem({ ...item }, collection);
  const list = await fetchJsonCollection(collection);
  const idx = list.findIndex((x) => String(x.id) === String(ui.id));
  if (idx >= 0) list[idx] = { ...list[idx], ...ui };
  else {
    ui.sort_order = list.length;
    list.push(ui);
  }
  await writeJsonCollection(collection, list);
  await syncTableBestEffort(collection, list);
  return ui;
}

export async function deleteItem(collection, id) {
  const list = (await fetchJsonCollection(collection)).filter(
    (x) => String(x.id) !== String(id)
  );
  const ordered = list.map((item, index) => ({ ...item, sort_order: index }));
  await writeJsonCollection(collection, ordered);
  if (await tablesAvailable(collection)) {
    try {
      await supabase.from(collection).delete().eq("id", id);
    } catch {
      // ignore table delete failures
    }
  }
  await syncTableBestEffort(collection, ordered);
}

export async function saveCollectionOrder(collection, orderedItems) {
  const ordered = orderedItems.map((item, index) => ({
    ...toUiItem(item, collection),
    sort_order: index,
  }));
  await writeJsonCollection(collection, ordered);
  await syncTableBestEffort(collection, ordered);
  return ordered;
}

export async function uploadWorkImage(collection, id, file) {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const safeId = String(id).replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `images/${collection}/${safeId}.${ext}`;

  const { error } = await supabase.storage.from("media").upload(path, file, {
    upsert: true,
    contentType: file.type || "image/jpeg",
  });
  if (error) throw error;

  const { data } = supabase.storage.from("media").getPublicUrl(path);
  return `${data.publicUrl}?t=${Date.now()}`;
}

export async function listLandingMedia() {
  const list = await listCollection(LANDING_MEDIA_COLLECTION);
  const byId = Object.fromEntries(
    (list || []).map((item) => [String(item.id), item])
  );
  return LANDING_MEDIA_SLOTS.map((slot, index) => ({
    ...slot,
    id: slot.id,
    title: byId[slot.id]?.title || slot.label,
    ImageUrl: byId[slot.id]?.ImageUrl || "",
    sort_order: byId[slot.id]?.sort_order ?? index,
  }));
}

export async function saveLandingMediaSlot(id, imageUrl) {
  const slot = LANDING_MEDIA_SLOTS.find((item) => item.id === id);
  if (!slot) throw new Error("Unknown landing image slot");
  return upsertItem(LANDING_MEDIA_COLLECTION, {
    id: slot.id,
    title: slot.label,
    ImageUrl: imageUrl || "",
    sort_order: LANDING_MEDIA_SLOTS.findIndex((item) => item.id === id),
  });
}
